import { useEffect, useState, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { debounce } from 'lodash';
import { DEBOUNCE_TIME } from 'lib/constants';
import { GeocodeFeature, GeocodeQueryType } from '@mapbox/mapbox-sdk/services/geocoding';
import { locationSearch } from 'lib/mapbox-utils/mapboxUtils';
import { DistanceSelect } from './DistanceSelect';

interface Props {
  onChange: (value: Partial<GeocodeFeature> | null) => void;
  defaultValue?: Partial<GeocodeFeature>;
  id?: string;
  label?: string;
  size?: 'medium' | 'small';
  locationTypes?: GeocodeQueryType[];
  includeDistance?: boolean;
  className?: string;
  placeholder?: string;
  defaultDistance?: number;
}

export function LocationAutocomplete({
  onChange,
  defaultValue,
  className,
  id,
  label,
  placeholder,
  size = 'medium',
  locationTypes = ['place', 'locality', 'neighborhood'],
  includeDistance = false,
  defaultDistance,
}: Props) {
  const [value, setValue] = useState<Partial<GeocodeFeature> | undefined | null>(defaultValue);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Partial<GeocodeFeature>[]>([]);

  const fetchLocations = useMemo(
    () => debounce(async (
      searchStr: string, callback: (results?: Partial<GeocodeFeature>[]) => void,
    ) => {
      const results = await locationSearch(searchStr, locationTypes);
      callback(results);
    }, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    if (!defaultValue?.place_name) {
      // When defaultValue changes and is empty, clear everything.
      setInputValue('');
      setValue(null);
      setOptions([]);
    } else {
      setInputValue(defaultValue.place_name || '');
      setValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions(value?.place_name ? [value] : []);
      return undefined;
    }

    fetchLocations(inputValue, (results?: Partial<GeocodeFeature>[]) => {
      if (active) {
        let newOptions = [] as Partial<GeocodeFeature>[];

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [defaultValue, inputValue, fetchLocations]);

  return (
    <>
      <Autocomplete
        id="location-autocomplete"
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.place_name || ''}
        options={options}
        className={className}
        autoComplete
        filterSelectedOptions
        value={value || null}
        isOptionEqualToValue={(option, v) => option?.place_name === v?.place_name}
        onChange={(_, newValue: Partial<GeocodeFeature> | null) => {
          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
          onChange(newValue);
          setInputValue(newValue?.place_name || '');
        }}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <div className="flex">
            <TextField
              {...params}
              label={label}
              id={id}
              placeholder={placeholder}
              fullWidth
              variant="outlined"
              size={size}
            />
            {includeDistance && !!defaultValue
            && <DistanceSelect defaultDistance={defaultDistance} />}

          </div>
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <div className="flex w-full">
              <div className="w-20">
                <LocationOnIcon />
              </div>
              <div className="w-4/5">
                <div className="font-bold">
                  {option.text}
                </div>
                <div className="text-mediumgray text-sm">
                  {option.place_name}
                </div>
              </div>
            </div>
          </li>
        )}
        noOptionsText="ex: Denver, Colorado"
      />
    </>

  );
}
